import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminContentEditorFields,
  AdminPageHeader,
  appendYowanContentPreviewQuery,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  fetchYowanAdminNewsDetail,
  getYowanAdminMutationErrorMessage,
  updateYowanAdminNews,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const AdminNewsEditPage = () => {
  const content = yowanPageContent['/admin/content/news/:newsId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { newsId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord || {
        title: '',
        slug: '',
        isPublished: false,
        notify_on_publish: false,
        publishedAt: '',
        notificationScheduledAt: '',
        excerpt: '',
        body: '',
        primary_image_id: '',
      },
  });

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const updated = await updateYowanAdminNews(newsId, formValues);
      setSubmitMessage('保存しました。');
      if (updated?.id || newsId) {
        navigate(appendYowanContentPreviewQuery('/admin/content/news', preservedDomainSearch));
      }
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setSubmitMessage('');

    fetchYowanAdminNewsDetail(newsId)
      .then((record) => {
        if (isMounted) {
          setCurrentRecord(record);
          reset(record);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCurrentRecord(null);
          setSubmitMessage('お知らせを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [newsId, reset]);

  if (!content) {
    return null;
  }

  if (!currentRecord) {
    return (
      <AdminBlogShell activeNavHref="/admin/content/news" routeKey="/admin/content/news/:newsId/edit">
        <AdminPageHeader
          title={content.header.title}
          paragraphs={content.header.paragraphs}
          actions={[
            <Link key="back-to-list" to={appendYowanContentPreviewQuery('/admin/content/news', preservedDomainSearch)}>
              一覧へ戻る
            </Link>,
          ]}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <p className="yowan-admin-form__message">{submitMessage || 'お知らせが見つかりませんでした。'}</p>
        )}
      </AdminBlogShell>
    );
  }

  return (
    <AdminBlogShell activeNavHref="/admin/content/news" routeKey="/admin/content/news/:newsId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanContentPreviewQuery('/admin/content/news', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminContentEditorFields
          content={content}
          register={register}
          errors={errors}
          requireTitleAndSlug={false}
          slugReadOnly
        />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {content.form.submitLabel}
          </button>
        </div>

        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminNewsEditPage;
